import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import React from 'react';

import { cx } from '../../../utils/cx';
import styles from './Select.module.scss';

export interface SelectOption<T extends string | number> {
    value: T;
    label: string;
    hint?: string;
}

export interface SelectProps<T extends string | number> {
    value: T;
    options: SelectOption<T>[];
    onChange: (value: T) => void;
    'aria-label': string;
    onOpenChange?: (open: boolean) => void;
    placeholder?: string;
    emptyText?: string;
    disabled?: boolean;
    loading?: boolean;
    invalid?: boolean;
    id?: string;
    className?: string;
    /** По умолчанию поле поиска появляется у списков от SEARCH_THRESHOLD пунктов. */
    searchable?: boolean;
    searchPlaceholder?: string;
}

const TYPEAHEAD_RESET_MS = 600;
const POPUP_GAP = 4;
const POPUP_MIN_HEIGHT = 200;

/* Короткий список перебирается глазами быстрее, чем набирается. */
const SEARCH_THRESHOLD = 8;

export function Select<T extends string | number>({
    value,
    options,
    onChange,
    'aria-label': ariaLabel,
    onOpenChange,
    placeholder = 'Выберите значение',
    emptyText = 'Список пуст',
    disabled = false,
    loading = false,
    invalid = false,
    id,
    className,
    searchable,
    searchPlaceholder = 'Начните вводить название',
}: SelectProps<T>) {
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [query, setQuery] = useState('');
    const [popupStyle, setPopupStyle] = useState<CSSProperties | null>(null);
    const [openUp, setOpenUp] = useState(false);

    const rootRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const typed = useRef({ text: '', at: 0 });

    const withSearch = searchable ?? options.length >= SEARCH_THRESHOLD;

    const selected = options.find((option) => option.value === value) ?? null;
    const listId = id ?? 'select';

    /* Ищем по вхождению, а не по началу строки: «ненецкий» должен находить
       и Ямало-Ненецкий, и Ханты-Мансийский АО. */
    const shown = useMemo(() => {
        const text = query.trim().toLowerCase();
        if (!withSearch || !text) return options;
        return options.filter(
            (option) =>
                option.label.toLowerCase().includes(text) ||
                (option.hint?.toLowerCase().includes(text) ?? false),
        );
    }, [options, query, withSearch]);

    const toggle = (next: boolean) => {
        setOpen(next);
        onOpenChange?.(next);
    };

    const close = (returnFocus: boolean) => {
        toggle(false);
        setQuery('');
        if (returnFocus) buttonRef.current?.focus();
    };

    const place = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;

        const spaceBelow = window.innerHeight - rect.bottom;
        const up = spaceBelow < POPUP_MIN_HEIGHT && rect.top > spaceBelow;

        setOpenUp(up);

        setPopupStyle({
            position: 'fixed',
            left: rect.left,
            minWidth: rect.width,
            ...(up
                ? { bottom: window.innerHeight - rect.top + POPUP_GAP }
                : { top: rect.bottom + POPUP_GAP }),
        });
    };

    useLayoutEffect(() => {
        if (!open) {
            setPopupStyle(null);
            return undefined;
        }

        place();

        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);

        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideRoot = rootRef.current?.contains(target);
            const insidePopup = popupRef.current?.contains(target);

            /* Клик мимо — фокус не возвращаем: человек уже перевёл его сам. */
            if (!insideRoot && !insidePopup) close(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    /* Фокус в поле поиска: иначе первая набранная буква уйдёт кнопке. */
    useEffect(() => {
        if (open && withSearch) searchRef.current?.focus();
    }, [open, withSearch]);

    useEffect(() => {
        if (!open) return;
        const item = listRef.current?.children[cursor] as HTMLElement | undefined;
        item?.scrollIntoView({ block: 'nearest' });
    }, [cursor, open]);

    const openList = () => {
        if (disabled || loading) return;
        setQuery('');
        const index = options.findIndex((option) => option.value === value);
        setCursor(index >= 0 ? index : 0);
        toggle(true);
    };

    const pick = (index: number) => {
        const option = shown[index];
        if (!option) return;
        onChange(option.value);
        close(true);
    };

    const onQueryChange = (next: string) => {
        setQuery(next);
        /* Список поменялся — прежний индекс указывает на другой пункт. */
        setCursor(0);
    };

    /* Список без поиска: прыжок по первой букве. */
    const runTypeahead = (char: string) => {
        if (options.length === 0) return;

        const now = Date.now();
        const text =
            (now - typed.current.at > TYPEAHEAD_RESET_MS ? '' : typed.current.text) +
            char.toLowerCase();
        typed.current = { text, at: now };

        const selectedIndex = options.findIndex((option) => option.value === value);
        const from = open ? cursor : Math.max(selectedIndex, 0);

        for (let step = 1; step <= options.length; step += 1) {
            const index = (from + step) % options.length;
            if (options[index].label.toLowerCase().startsWith(text)) {
                if (open) setCursor(index);
                else pick(index);
                return;
            }
        }
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape' || event.key === 'Tab') {
            if (!open) return;
            /* Esc возвращает фокус кнопке, Tab уводит дальше по форме. */
            close(event.key === 'Escape');
            return;
        }

        const isPrintable =
            event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

        if (!open) {
            if (
                event.key === 'ArrowDown' ||
                event.key === 'ArrowUp' ||
                event.key === 'Enter' ||
                event.key === ' '
            ) {
                event.preventDefault();
                openList();
                return;
            }

            if (!withSearch && isPrintable) {
                event.preventDefault();
                runTypeahead(event.key);
            }
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setCursor((prev) => Math.min(prev + 1, shown.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setCursor((prev) => Math.max(prev - 1, 0));
            return;
        }
        if (event.key === 'Home') {
            event.preventDefault();
            setCursor(0);
            return;
        }
        if (event.key === 'End') {
            event.preventDefault();
            setCursor(shown.length - 1);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            pick(cursor);
            return;
        }

        /* Пробел выбирает только там, где его негде набрать. */
        if (event.key === ' ') {
            if (withSearch) return;
            event.preventDefault();
            pick(cursor);
            return;
        }

        if (!withSearch && isPrintable) {
            event.preventDefault();
            runTypeahead(event.key);
        }
    };

    const search = withSearch && (
        <div className={cx(styles.search, openUp && styles.searchBottom)}>
            <input
                ref={searchRef}
                className={styles.searchInput}
                type="text"
                role="combobox"
                autoComplete="off"
                placeholder={searchPlaceholder}
                aria-label={`${ariaLabel}: поиск`}
                aria-controls={`${listId}-list`}
                aria-expanded
                aria-activedescendant={shown[cursor] ? `${listId}-option-${cursor}` : undefined}
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={onKeyDown}
            />
        </div>
    );

    const list =
        shown.length === 0 ? (
            <div className={styles.empty}>
                {options.length === 0 ? emptyText : 'Ничего не найдено'}
            </div>
        ) : (
            <ul className={styles.list} role="listbox" id={`${listId}-list`} ref={listRef}>
                {shown.map((option, index) => (
                    <li
                        key={String(option.value)}
                        id={`${listId}-option-${index}`}
                        role="option"
                        aria-selected={option.value === value}
                        className={
                            index === cursor
                                ? styles.optionActive
                                : option.value === value
                                    ? styles.optionSelected
                                    : styles.option
                        }
                        onMouseEnter={() => setCursor(index)}
                        onClick={() => pick(index)}
                    >
                        <span className={styles.optionLabel}>{option.label}</span>
                        {option.hint && <span className={styles.optionHint}>{option.hint}</span>}
                    </li>
                ))}
            </ul>
        );

    const popup = open && popupStyle && (
        <div className={styles.popup} ref={popupRef} style={popupStyle}>
            {/* Попап раскрылся вверх — поле поиска ставим снизу, вплотную
                к кнопке: иначе ввод оказывается на другом краю экрана. */}
            {openUp ? (
                <>
                    {list}
                    {search}
                </>
            ) : (
                <>
                    {search}
                    {list}
                </>
            )}
        </div>
    );

    return (
        <div className={cx(styles.root, className)} ref={rootRef}>
            <button
                type="button"
                id={id}
                ref={buttonRef}
                className={invalid ? styles.controlInvalid : styles.control}
                disabled={disabled || loading}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-activedescendant={
                    open && !withSearch && shown[cursor]
                        ? `${listId}-option-${cursor}`
                        : undefined
                }
                onClick={() => (open ? close(false) : openList())}
                onKeyDown={onKeyDown}
            >
                <span className={selected ? styles.value : styles.placeholder}>
                    {loading ? 'Загружаем…' : (selected?.label ?? placeholder)}
                </span>
                <i className={open ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
            </button>

            {popup && createPortal(popup, document.body)}
        </div>
    );
}